import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ComboboxInput,
  Combobox as HeadlessCombobox,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react';
import Spinner from '@dashboard/components/Spinner';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';
import Label from '@dashboard/components/Form/components/Label';

const propTypes = {
  name: PropTypes.string,
  value: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  immediate: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.any,
  placeholder: PropTypes.string,
  onChangeInput: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  hasItems: PropTypes.bool.isRequired,
  noItemsMsg: PropTypes.string,
  fetchNextPage: PropTypes.func,
  showLabel: PropTypes.bool,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  onClear: PropTypes.func,
  showSearchIcon: PropTypes.bool,
};

const defaultProps = {
  value: '',
  noItemsMsg: 'No items found',
  showSearchIcon: true,
};

const Combobox = props => {
  const {
    onClose,
    immediate,
    value,
    onChange,
    placeholder,
    isError,
    isLoading,
    hasItems,
    children,
    onChangeInput,
    noItemsMsg,
    fetchNextPage,
    tooltip,
    label,
    showLabel,
    name,
    onClear,
    showSearchIcon,
    onFocus,
    onBlur,
  } = props;
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState();
  const [dropdownPosition, setDropdownPosition] = useState('below');
  const [inputHeight, setInputHeight] = useState(0);

  const calculatePosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const currentInputHeight = rect.height;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedDropdownHeight = 288; // max-h-72 = 288px
      const totalSpaceNeeded = estimatedDropdownHeight + 8; // Add 8px to account for `mb-2` although we apply this with a style.

      setInputHeight(currentInputHeight);

      if (spaceBelow < totalSpaceNeeded && spaceAbove > totalSpaceNeeded) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
  };

  useEffect(() => {
    if (isFocused) {
      const handleResize = () => calculatePosition();
      const handleScroll = () => calculatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isFocused]);

  useEffect(() => {
    if (isFocused) {
      if (onFocus) onFocus();
    } else {
      if (onBlur) onBlur();
    }
  }, [isFocused, onFocus, onBlur]);

  return (
    <Fragment>
      <HeadlessCombobox
        immediate={immediate ?? false}
        value={value?._id}
        onChange={value => {
          onChange(value);
          /**
           * We need to wait for the blur event to fire before closing the combobox after selection.
           * Otherwise, the comboboxoptions will not close.
           */
          setTimeout(() => {
            if (value !== null) inputRef.current?.blur();
          }, 0);
        }}
        onClose={onClose}
      >
        {showLabel && (
          <Label htmlFor={name} tooltip={tooltip}>
            {label}
          </Label>
        )}
        <div className="relative">
          <ComboboxInput
            ref={inputRef}
            className="flex justify-between items-center w-full rounded-sm shadow-input py-1.5 pr-10 border-0 focus:outline-none pointer-events-auto"
            aria-label={placeholder}
            displayValue={() => value?.name}
            onChange={onChangeInput}
            placeholder={placeholder}
            onFocus={() => {
              setIsFocused(true);
              calculatePosition();
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
            autocomplete="off"
          />

          <div className="absolute inset-y-1 right-2 flex items-center bg-white pointer-events-auto">
            {!isFocused && inputRef.current?.value.trim().length > 0 && (
              <button
                onClick={e => {
                  onClear(e);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 0);
                }}
                className="pr-2 text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}

            {showSearchIcon && <MagnifyingGlassIcon className="text-gray-300 w-5 h-5" />}
          </div>
        </div>

        <ComboboxOptions>
          <div className="relative">
            <div
              className={`absolute bg-white border border-gray-200 rounded w-full z-10 max-h-72 overflow-auto shadow-sm ${
                dropdownPosition === 'above' ? 'bottom-full' : 'mt-2'
              }`}
              style={
                dropdownPosition === 'above' ? { marginBottom: `${inputHeight + 8}px` } : undefined
              }
            >
              {(isError || (!isLoading && !hasItems)) && (
                <ComboboxOption className="p-2">{noItemsMsg}</ComboboxOption>
              )}

              <BottomScrollListener onBottom={fetchNextPage}>{children}</BottomScrollListener>

              {isLoading && (
                <div className="p-2">
                  <Spinner />
                </div>
              )}
            </div>
          </div>
        </ComboboxOptions>
      </HeadlessCombobox>
    </Fragment>
  );
};

Combobox.propTypes = propTypes;
Combobox.defaultProps = defaultProps;

export default Combobox;
